import {
  Person,
  PersonCapabilityRequestDTO,
  useAddCapabilitiesToPersonMutation,
  useCreatePersonMutation,
  useDeletePersonMutation,
  useRemoveCapabilitiesFromPersonMutation,
  useUpdatePersonMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";
import {EntityMutationCallback} from "./useEntityMutation";

export type UsePersonMutationProps = {}

export type UsePersonMutationResult = {
  createPerson: (person: Partial<Person>, callback: EntityMutationCallback<Person> | undefined) => void
  updatePerson: (person: Partial<Person>, callback: EntityMutationCallback<Person> | undefined) => void
  deletePerson: (personId: string, callback: EntityMutationCallback | undefined) => void
  addCapabilities: (personId: string, data: PersonCapabilityRequestDTO, callback: EntityMutationCallback | undefined) => void
  removeCapabilities: (personId: string, data: PersonCapabilityRequestDTO, callback: EntityMutationCallback | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
  isAddingCapabilities?: boolean
  isRemovingCapabilities?: boolean
}

export const usePersonMutation = (props: UsePersonMutationProps = {}): UsePersonMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)
  const [_addingCapabilities, _setAddingCapabilities] = useDebouncedState(false, 500)
  const [_removingCapabilities, _setRemovingCapabilities] = useDebouncedState(false, 500)

  // possible mutations
  const [_createPerson, {isLoading: isCreating}] = useCreatePersonMutation()
  const [_updatePerson, {isLoading: isUpdating}] = useUpdatePersonMutation()
  const [_deletePerson, {isLoading: isDeleting}] = useDeletePersonMutation()
  const [_addCapabilities, {isLoading: isAddingCapabilities}] = useAddCapabilitiesToPersonMutation()
  const [_removeCapabilities, {isLoading: isRemovingCapabilities}] = useRemoveCapabilitiesFromPersonMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    _setAddingCapabilities(isAddingCapabilities)
  }, [isAddingCapabilities, _setAddingCapabilities])

  useEffect(() => {
    _setRemovingCapabilities(isRemovingCapabilities)
  }, [isRemovingCapabilities, _setRemovingCapabilities])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "usePersonMutation_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.person", "Person"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("usePersonMutation_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating || _addingCapabilities || _removingCapabilities) {
      notifications.show({
        id: "usePersonMutation_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.person", "Person"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("usePersonMutation_updating")
    }
  }, [_updating, _addingCapabilities, _removingCapabilities, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "usePersonMutation_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.person", "Person"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("usePersonMutation_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<Person>, callback: EntityMutationCallback<Person> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.person", "Person") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })
          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback<Person> | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.person", "Person") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })
          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.person", "Person") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteConfirm = useCallback((personId: string, callback: EntityMutationCallback | undefined) => {
    const promise = _deletePerson({id: personId}).unwrap()
    handleDeleteResult(promise, callback)
  }, [_deletePerson, handleDeleteResult])

  const createPerson = useCallback((person: Partial<Person>, callback: EntityMutationCallback<Person> | undefined) => {
    const promise = _createPerson(person).unwrap()
    handleCreateResult(promise, callback)
  }, [_createPerson, handleCreateResult])

  const updatePerson = useCallback((person: Partial<Person>, callback: EntityMutationCallback<Person> | undefined) => {
    const promise = _updatePerson(person).unwrap()
    handleUpdateResult(promise, callback)
  }, [_updatePerson, handleUpdateResult])

  const deletePerson = useCallback((personId: string, callback: EntityMutationCallback | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.person", "Person"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.deleteperson.confirm", "Are you sure you want to delete this person?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(personId, callback)
      },
    })
  }, [handleDeleteConfirm, t])

  const addCapabilities = useCallback((personId: string, data: PersonCapabilityRequestDTO, callback: EntityMutationCallback | undefined) => {
    const promise = _addCapabilities({personId, data}).unwrap()
    handleUpdateResult(promise, callback)
  }, [_addCapabilities, handleUpdateResult])

  const removeCapabilities = useCallback((personId: string, data: PersonCapabilityRequestDTO, callback: EntityMutationCallback | undefined) => {
    const promise = _removeCapabilities({personId, data}).unwrap()
    handleUpdateResult(promise, callback)
  }, [_removeCapabilities, handleUpdateResult])

  return {
    createPerson: createPerson,
    updatePerson: updatePerson,
    deletePerson: deletePerson,
    addCapabilities: addCapabilities,
    removeCapabilities: removeCapabilities,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
    isAddingCapabilities: _addingCapabilities,
    isRemovingCapabilities: _removingCapabilities,
  }
}
